import { useEffect, useState, type FormEvent } from 'react'
import { Modal } from './Modal'
import { supabase } from '@/lib/supabase'
import { formatQ } from '@/lib/format'

/**
 * Modal de anulación de movimiento de caja.
 *
 * Append-only: NO borra el movimiento, solo lo marca como anulado con
 * motivo + usuario que autorizó. Las boletas asociadas vuelven a 'abierta'
 * vía trigger fn_recalcular_boleta. Los saldos de cuentas se reversan
 * insertando movimiento_cuenta tipo 'reversa'.
 *
 * Validación: motivo de mínimo 4 caracteres (lo enforza también el RPC).
 */

type Props = {
  open: boolean
  onClose: () => void
  movimientoId: string
  correlativo: number
  serie: string
  monto: number
  onSuccess: () => void
}

export function AnularMovimientoModal({
  open, onClose, movimientoId, correlativo, serie, monto, onSuccess,
}: Props) {
  const [motivo, setMotivo] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setMotivo('')
    setError(null)
  }, [open])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    if (motivo.trim().length < 4) {
      setError('El motivo debe tener al menos 4 caracteres.')
      return
    }

    setSubmitting(true)
    const { error: rpcErr } = await supabase.rpc('anular_movimiento_caja', {
      p_movimiento_id: movimientoId,
      p_motivo: motivo.trim(),
    })
    setSubmitting(false)

    if (rpcErr) {
      setError(`No se pudo anular: ${rpcErr.message}`)
      return
    }

    onSuccess()
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Anular movimiento"
      subtitle={`${serie}-${String(correlativo).padStart(4, '0')} · ${formatQ(monto)}`}
      size="sm"
      blocking={submitting}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button
            type="submit"
            form="anular-movimiento-form"
            className="btn btn-primary"
            disabled={submitting || motivo.trim().length < 4}
            style={{ background: '#dc2626' }}
          >
            {submitting ? 'Anulando…' : 'Anular movimiento'}
          </button>
        </>
      }
    >
      <form id="anular-movimiento-form" onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error && <div className="error-banner" style={{ margin: 0 }}>{error}</div>}

        <div
          style={{
            padding: '12px 14px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 10,
            fontSize: 12,
            color: '#991b1b',
            lineHeight: 1.55,
          }}
        >
          <strong style={{ fontWeight: 700 }}>Esta acción es append-only.</strong> El movimiento queda marcado
          como anulado en el histórico con tu nombre y el motivo. Si había boleta
          bancaria, se libera automáticamente. Si afectó saldos, se revierten.
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted-foreground)', letterSpacing: 0.4, textTransform: 'uppercase' }}>
            Motivo de la anulación
          </span>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ej. Error de captura · Cliente solicitó devolución · Movimiento duplicado"
            rows={3}
            required
            minLength={4}
            style={{
              resize: 'vertical', minHeight: 70,
              fontSize: 13, fontFamily: 'inherit',
            }}
          />
          <span style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
            Mínimo 4 caracteres. Queda en el histórico permanente.
          </span>
        </label>
      </form>
    </Modal>
  )
}
